// src/components/StockManagement/StockManagement.jsx
import React, { useState, useEffect } from 'react';
import stockManagementService from '../../services/stockManagementService';
import StockManagementTable from './StockManagementTable';
import StockManagementInModal from './StockManagementInModal';
import StockManagementOutModal from './StockManagementOutModal';
import { showNotification } from '../../utils/notification';
import StockUpdateModal from './StockUpdateModal';
import './StockManagement.css';
import { PlusCircle } from 'lucide-react';
import '../Inventory/inventory.css'; // Use same CSS as inventory

const StockManagement = ({ toggleSidebar }) => {
  // Store ALL data (no pagination)
  const [allStockLog, setAllStockLog] = useState([]);
  const [allStocks, setAllStocks] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  
  const [categories, setCategories] = useState([]);
  const [inventories, setInventories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isStockInOpen, setIsStockInOpen] = useState(false);
  const [isStockOutOpen, setIsStockOutOpen] = useState(false);
  const [isCurrentStockView, setIsCurrentStockView] = useState(false);
  const [isStockUpdateOpen, setIsStockUpdateOpen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  const [filters, setFilters] = useState({
    search: '',
    stockType: '',
    inventory: '',
    category: '',
    dateFrom: '',
    dateTo: ''
  });

  // Load all data on mount (no pagination)
  useEffect(() => {
    loadData();
  }, []);

  // Auto-filter whenever filters or view mode changes
  useEffect(() => {
    applyFilters();
    setCurrentPage(1); // Reset to first page when filters change
  }, [filters, allStocks, allStockLog, isCurrentStockView]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      
      console.log('🔄 Loading all data...');
      
      const [cats, invs, currentStocks, movementRecords] = await Promise.all([
        stockManagementService.getActiveCategories(),
        stockManagementService.getActiveInventories(),
        stockManagementService.getAllStocks(1, 100000),  // Load ALL current stocks
        stockManagementService.getStocks(1, 100000)      // Load ALL movement records
      ]);
      
      console.log('✅ Data loaded successfully!');
      console.log('📦 Categories:', cats?.length, 'items');
      console.log('🏢 Inventories:', invs?.length, 'items');
      console.log('📊 Current Stocks:', currentStocks?.length, 'items');
      console.log('📊 Movement Records:', movementRecords?.length, 'items');
      
      setCategories(cats || []);
      setInventories(invs || []);
      setAllStocks(Array.isArray(currentStocks) ? currentStocks : []);
      setAllStockLog(Array.isArray(movementRecords) ? movementRecords : []);
      
      showNotification(`Loaded ${currentStocks?.length || 0} stocks, ${movementRecords?.length || 0} movements`, 'success');
    } catch (error) {
      console.error('❌ LOAD DATA ERROR:', error);
      showNotification(`Failed to load: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Client-side filtering (real-time)
  const applyFilters = () => {
    let filtered = [];

    if (isCurrentStockView) {
      // Filter Current Stock View
      const stocksArray = Array.isArray(allStocks) ? allStocks : [];
      
      filtered = stocksArray.map(item => ({
        id: item.id,
        inventory: item.inventory,
        category: item.product?.category,
        productName: item.product?.name,
        qty: item.qty,
        pricePerUnit: item.pricePerUnit,
        unitCost: item.unitCost,
        createdAt: item.createdAt,
        calculateTotal: true
      }));

      // Apply inventory filter
      if (filters.inventory) {
        filtered = filtered.filter(item => 
          item.inventory?.id == filters.inventory
        );
      }

      // Apply category filter
      if (filters.category) {
        filtered = filtered.filter(item => 
          item.category?.id == filters.category
        );
      }

      // Apply search filter
      if (filters.search.trim()) {
        const searchLower = filters.search.toLowerCase().trim();
        filtered = filtered.filter(item =>
          (item.productName && item.productName.toLowerCase().includes(searchLower)) ||
          (item.inventory?.name && item.inventory.name.toLowerCase().includes(searchLower)) ||
          String(item.id).includes(searchLower)
        );
      }

    } else {
      // Filter Movement Log View
      const stocksArray = Array.isArray(allStockLog) ? allStockLog : [];
      const currentStocks = Array.isArray(allStocks) ? allStocks : [];
      
      filtered = stocksArray.map(item => {
        let type = item.type || 'UNKNOWN';
        let inventoryId = null;
        
        if (type === 'UNKNOWN') {
          if (item.fromInventoryId === null && item.toInventoryId !== null) {
            type = 'IN';
            inventoryId = item.toInventoryId;
          } else if (item.fromInventoryId !== null && item.toInventoryId === null) {
            type = 'OUT';
            inventoryId = item.fromInventoryId;
          } else if (item.fromInventoryId !== null && item.toInventoryId !== null) {
            type = 'TRANSFER';
            inventoryId = item.fromInventoryId;
          }
        } else {
          inventoryId = type === 'IN' ? item.toInventoryId : item.fromInventoryId;
        }
        
        const fromInventory = item.fromInventoryId ? inventories.find(inv => inv.id === item.fromInventoryId) : null;
        const toInventory = item.toInventoryId ? inventories.find(inv => inv.id === item.toInventoryId) : null;
        const stockInfo = currentStocks.find(stock => 
          stock.product?.id === item.productId && 
          stock.inventory?.id === inventoryId
        );
        const category = stockInfo?.product?.category;
        
        return {
          ...item,
          type: type,
          uniqueId: `${type}_${item.id}`,
          fromInventory: fromInventory || (item.fromInventoryId ? { id: item.fromInventoryId, name: 'Unknown' } : null),
          toInventory: toInventory || (item.toInventoryId ? { id: item.toInventoryId, name: 'Unknown' } : null),
          inventory: fromInventory || toInventory || { id: inventoryId, name: 'Unknown' },
          category: category || { id: item.categoryId, name: 'Unknown' },
          productName: item.productNameAtTime,
          pricePerUnit: stockInfo?.pricePerUnit || item.pricePerUnit || 0,
          unitCost: stockInfo?.unitCost || item.unitCost || 0,
          createdAt: item.createdAt || item.date
        };
      });

      // Filter by stock type
      if (filters.stockType) {
        filtered = filtered.filter(item => item.type === filters.stockType);
      }

      // Filter by inventory
      if (filters.inventory) {
        filtered = filtered.filter(item => item.inventory?.id == filters.inventory);
      }

      // Filter by category
      if (filters.category) {
        filtered = filtered.filter(item => item.category?.id == filters.category);
      }

      // Filter by date range
      if (filters.dateFrom) {
        filtered = filtered.filter(item => {
          const itemDate = item.createdAt || item.date;
          if (!itemDate) return false;
          const itemDateOnly = new Date(itemDate).toISOString().split('T')[0];
          return itemDateOnly >= filters.dateFrom;
        });
      }
      if (filters.dateTo) {
        filtered = filtered.filter(item => {
          const itemDate = item.createdAt || item.date;
          if (!itemDate) return false;
          const itemDateOnly = new Date(itemDate).toISOString().split('T')[0];
          return itemDateOnly <= filters.dateTo;
        });
      }

      // Filter by search
      if (filters.search.trim()) {
        const searchLower = filters.search.toLowerCase().trim();
        filtered = filtered.filter(item =>
          String(item.id).includes(searchLower) ||
          (item.productName && item.productName.toLowerCase().includes(searchLower)) ||
          (item.inventory?.name && item.inventory.name.toLowerCase().includes(searchLower)) ||
          String(item.qty).includes(searchLower)
        );
      }
    }

    setFilteredData(filtered);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      stockType: '',
      inventory: '',
      category: '',
      dateFrom: '',
      dateTo: ''
    });
    showNotification('All filters cleared', 'success');
  };

  const toggleViewMode = () => {
    setIsCurrentStockView(!isCurrentStockView);
    setCurrentPage(1); // Reset to first page when switching views
  };

  const handleStockInSave = async (payload) => {
    try {
      setIsSaving(true);
      await stockManagementService.stockIn(payload);
      showNotification('Stock In saved successfully!', 'success');
      setIsStockInOpen(false);
      await loadData();
    } catch (error) {
      showNotification(`Failed: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStockOutSave = async () => {
    try {
      setIsSaving(true);
      showNotification('Stock Out processed successfully!', 'success');
      setIsStockOutOpen(false);
      await loadData();
    } catch (error) {
      showNotification(`Failed: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleStockUpdateSave = async () => {
    await loadData();
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <main className="stock-main-content">
      <header className="content-header">
        <button className="btn-toggle-sidebar" onClick={toggleSidebar}>☰</button>
        <h2 className="content-title" id="mainContentTitle">
          {isCurrentStockView ? 'Stock Management (Current Stock)' : 'Stock Management (Movement Log)'}
        </h2>
        <div className="header-actions">
          <button 
            className="btn btn-current-stock"
            id="viewCurrentStockBtn"
            onClick={toggleViewMode}
          >
            {isCurrentStockView ? 'View Movement Log' : 'View Current Stock'}
          </button>
          <button 
            className="inventory-btn inventory-btn-add" 
            id="openStockInBtn" 
            onClick={() => setIsStockInOpen(true)}
          >
            Stock In
          </button>
          <button 
            className="inventory-btn inventory-btn-add" 
            id="openStockOutBtn"
            onClick={() => setIsStockOutOpen(true)}
          >
            Stock Out
          </button>
        </div>
      </header>

      <section className="filter-controls">
        <div className="filter-group">
          <input 
            type="text" 
            className="custom-input search-input" 
            id="filterSearch" 
            placeholder="Search ID, Item, Vendor"
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            style={{ flex: 1, minWidth: '250px' }}
          />
          
          <select 
            className="custom-select" 
            id="filterStockType"
            name="stockType"
            value={filters.stockType}
            onChange={handleFilterChange}
            disabled={isCurrentStockView}
          >
            <option value="">All Movements</option>
            <option value="IN">Stock IN</option>
            <option value="OUT">Stock OUT</option>
          </select>

          <select 
            className="custom-select" 
            id="filterInventory"
            name="inventory"
            value={filters.inventory}
            onChange={handleFilterChange}
          >
            <option value="">Filter By Inventory</option>
            {inventories.map(inv => (
              <option key={inv.id} value={inv.id}>{inv.name}</option>
            ))}
          </select>

          <select 
            className="custom-select" 
            id="filterCategory"
            name="category"
            value={filters.category}
            onChange={handleFilterChange}
          >
            <option value="">Filter By Category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          <input 
            type="date" 
            className="custom-input date-input" 
            id="filterDateFrom"
            name="dateFrom"
            title="From Date"
            value={filters.dateFrom}
            onChange={handleFilterChange}
            disabled={isCurrentStockView}
          />

          <input 
            type="date" 
            className="custom-input date-input" 
            id="filterDateTo"
            name="dateTo"
            title="To Date"
            value={filters.dateTo}
            onChange={handleFilterChange}
            disabled={isCurrentStockView}
          />
          
          <button 
            className="inventory-btn inventory-btn-clear" 
            id="clearFilterBtn"
            onClick={clearFilters}
          >
            Clear Filters
          </button>
        </div>

        {/* Filter Summary */}
        <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
          Showing <strong>{startIndex + 1}-{Math.min(endIndex, filteredData.length)}</strong> of{' '}
          <strong>{filteredData.length}</strong> filtered results
          {filteredData.length !== (isCurrentStockView ? allStocks.length : allStockLog.length) && (
            <span> (from <strong>{isCurrentStockView ? allStocks.length : allStockLog.length}</strong> total)</span>
          )}
        </div>
      </section>

      <StockManagementTable
        stockManagements={paginatedData} 
        allFilteredData={filteredData}  
        isLoading={isLoading}
        viewType={isCurrentStockView ? 'CURRENT' : 'MOVEMENT'}
      />

      <div className="pagination-controls">
        <button 
          className="btn-page" 
          disabled={currentPage === 1} 
          onClick={handlePreviousPage}
        >
          Previous
        </button>
        
        <div className="page-numbers">
          {currentPage > 2 && (
            <>
              <button className="btn-page-num" onClick={() => handlePageChange(1)}>1</button>
              {currentPage > 3 && <span className="page-ellipsis">...</span>}
            </>
          )}
          
          {currentPage > 1 && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(currentPage - 1)}
            >
              {currentPage - 1}
            </button>
          )}
          
          <button className="btn-page-num active">{currentPage}</button>
          
          {currentPage < totalPages && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(currentPage + 1)}
            >
              {currentPage + 1}
            </button>
          )}
          
          {currentPage < totalPages - 1 && (
            <>
              {currentPage < totalPages - 2 && <span className="page-ellipsis">...</span>}
              <button className="btn-page-num" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
            </>
          )}
        </div>

        <span className="page-info">Page {currentPage} of {totalPages}</span>
        
        <button 
          className="btn-page" 
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={handleNextPage}
        >
          Next
        </button>
      </div>

      <StockManagementInModal
        isOpen={isStockInOpen} 
        onClose={() => setIsStockInOpen(false)} 
        onSave={handleStockInSave}
        isSaving={isSaving} 
        inventories={inventories || []}
        categories={categories || []}
      />

      <StockManagementOutModal
        isOpen={isStockOutOpen} 
        onClose={() => setIsStockOutOpen(false)} 
        onSave={handleStockOutSave}
        isSaving={isSaving} 
        categories={categories} 
        inventories={inventories} 
      />

      <StockUpdateModal
        isOpen={isStockUpdateOpen} 
        onClose={() => setIsStockUpdateOpen(false)} 
        onSave={handleStockUpdateSave}
        categories={categories} 
        inventories={inventories} 
      />
    </main>
  );
};

export default StockManagement;
